import React, { useState } from "react";
import { useAppDispatch } from "../../store/hook";
import { clearReconResult } from "../../features/recon/ReconSlice";
import SelpReconPanel from "./processors/SelpReconPanel";
import IciciReconPanel from "./processors/IciciReconPanel";

// ── Processor registry ────────────────────────────────────────────────────────

const PROCESSORS = [
  { value: "SELP",    label: "SELP",    available: true  },
  { value: "ICICI",   label: "ICICI",   available: true  },
  { value: "YESBANK", label: "Yes Bank", available: false },
] as const;

type ProcessorValue = (typeof PROCESSORS)[number]["value"];

// ── Placeholder for upcoming processors ──────────────────────────────────────

const ComingSoonPanel: React.FC<{ label: string }> = ({ label }) => (
  <div className="bg-white rounded-xl border border-cardborder p-12 flex flex-col items-center justify-center gap-3 text-center">
    <span className="text-4xl">🔜</span>
    <p className="text-base font-semibold text-gray-700">{label} reconciliation is coming soon</p>
    <p className="text-sm text-gray-400">Support for this processor will be added in a future release.</p>
  </div>
);

// ── Page ──────────────────────────────────────────────────────────────────────

const ReconPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [selectedProcessor, setSelectedProcessor] = useState<ProcessorValue>("SELP");

  const handleProcessorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as ProcessorValue;
    setSelectedProcessor(value);
    dispatch(clearReconResult());
  };

  return (
    <div className="bg-white h-full">
      {/* Header */}
      <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-7 py-8">
        <div className="flex items-center h-10">
          <h2 className="text-2xl font-bold text-gray-900">Reconciliation</h2>
        </div>
        <select
          value={selectedProcessor}
          onChange={handleProcessorChange}
          className="mt-4 md:mt-0 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand"
        >
          {PROCESSORS.map((p) => (
            <option key={p.value} value={p.value} disabled={!p.available}>
              {p.label}{!p.available ? " — Coming Soon" : ""}
            </option>
          ))}
        </select>
      </div>

      <div className="max-w-7xl mx-auto px-8 space-y-6">
        {selectedProcessor === "SELP"  && <SelpReconPanel />}
        {selectedProcessor === "ICICI" && <IciciReconPanel />}
        {selectedProcessor === "YESBANK" && (
          <ComingSoonPanel label={PROCESSORS.find((p) => p.value === selectedProcessor)!.label} />
        )}
      </div>
    </div>
  );
};

export default ReconPage;
